// ApplicationsList.jsx
import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSort,
  faSortUp,
  faSortDown,
  faEllipsisVertical,
  faEye,
  faTrash,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { Button } from 'react-bootstrap';
import ApplicationModal from '../ApplicationCard/ApplicationModal';
import { useDebounce } from '../../../../../utils/useDebounce';
import AdvancedFiltersAndColumns from './AdvancedFiltersAndColumns';

import { getBenefitsDictionary, getDisabilitiesList } from '../../../../../services/apiClient';
import { findKgsPaginated } from '../../../../cabinet/wizplain/activity';

import '../QueueFull/cabinet-zdo-requests.css';
import './applications-style.css';
import './AdvancedFilter.css';

//import { getRequestsList } from '../../../../statistic_public/actions';
import { getRequestsAdvanced } from './actions';
import { applicationApi } from '../ApplicationCard/api/applicationApi';
import { convertReqToForm } from '../../../../cabinet/wizplain/helpers/convReqToForm';
import { requestword } from '../../../../../components/helpers/wordsnumber';



const ApplicationsList = ({ mode, isMy, newApplication, onEdit, userRole }) => {
  const [sortConfig, setSortConfig] = useState({ key: 'submitted', direction: 'desc' });
  const [activeMenuIndex, setActiveMenuIndex] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [applicationsData, setApplicationsData] = useState([]);
  const [allcount, setAllCount] = useState(0);
  const [isLoad, setLoad] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Состояния для расширенных фильтров
  const [advancedFilters, setAdvancedFilters] = useState({});
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  
  // Глобальный поиск
  const [globalSearch, setGlobalSearch] = useState('');
  const debouncedGlobalSearch = useDebounce(globalSearch, 700);

  // Справочники
  const [benefitsList, setBenefitsList] = useState([]);
  const [disabilitiesList, setDisabilitiesList] = useState([]);
  
  // Состояние для видимых колонок (с сохранением в localStorage)
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('applications_visible_columns');
    if (saved) return JSON.parse(saved);
    return ['name', 'certificate', 'submitted', 'sendFrom', 'statusText', 'benefitText', 'dissabilities', 'note', 'actions'];
  });

  // Опции для фильтров (загружаем из API)
  const [districtOptions, setDistrictOptions] = useState([]);
  const [kindergartenOptions, setKindergartenOptions] = useState([]);

  // Счётчик запросов — чтобы не применять устаревший (пришедший позже, но запущенный раньше) ответ
  const requestIdRef = useRef(0);

  // Состояния для выбора садиков
  const [selectedKindergartensList, setSelectedKindergartensList] = useState([]);
  const [kgSearchTerm, setKgSearchTerm] = useState('');
  const [kgPage, setKgPage] = useState(0);
  const [kgHasMore, setKgHasMore] = useState(true);
  const [kgLoading, setKgLoading] = useState(false);
  const [kgAllOptions, setKgAllOptions] = useState([]);
  const [kgById, setKgById] = useState({});

  // Определение доступных колонок
  const availableColumns = [
    { key: 'name', label: "Ім'я дитини", sortable: true, width: '12%' },
    { key: 'certificate', label: 'Свідоцтво', sortable: true, width: '12%' },
    { key: 'submitted', label: 'Дата заявки', sortable: true, width: '12%' },
    { key: 'sendFrom', label: 'Надсилати з', sortable: true, width: '12%' },
    { key: 'statusText', label: 'Статус', sortable: true, width: '12%' },
    { key: 'benefitText', label: 'Пільга', sortable: true, width: '15%' },
    { key: 'dissabilities', label: 'ІРЦ', sortable: true, width: '10%' },
    { key: 'preferDate', label: 'Бажана дата', sortable: true, width: '10%' },
    { key: 'inviteDate', label: 'Дата запрошення', sortable: true, width: '10%' },
    { key: 'selectedKindergartens', label: 'Вибрані садочки', sortable: false, width: '15%' },
    { key: 'declineCount', label: 'Кількість відмов', sortable: true, width: '8%' },
    { key: 'age', label: 'Вік', sortable: true, width: '6%' },
   // { key: 'note', label: 'Примітка', sortable: false, width: '8%' },
    { key: 'actions', label: 'Дії', sortable: false, width: '8%' },
  ];

  // Загрузка справочников
  useEffect(() => {
    const loadDictionaries = async () => {
      try {
        const benefits = await getBenefitsDictionary();
        setBenefitsList(benefits || []);
        
        const disabilities = await getDisabilitiesList();
        setDisabilitiesList(disabilities || []);
      } catch (error) {
        console.error('Error loading dictionaries:', error);
      }
    };
    loadDictionaries();
  }, []);

  // Загрузка опций садиков для автокомплита
  const loadKindergartens = useCallback(async (search, pageNum = 0, append = false) => {
    if (pageNum === 0) setKgLoading(true);
    try {
      const result = await findKgsPaginated({ search, page: pageNum });
      const newOptions = result.items.map(item => ({
        value: item._id,
        label: item.name,
        ...item
      }));
      
      if (append) {
        setKgAllOptions(prev => [...prev, ...newOptions]);
      } else {
        setKgAllOptions(newOptions);
      }

      // накапливаем кеш независимо от пагинации
      setKgById(prev => {
        const next = { ...prev };
        newOptions.forEach(opt => { 
          next[opt.value] = { id: opt.value, name: opt.label };
        });
        return next;
      });

      setKgHasMore(result.hasMore);
      setKgPage(pageNum);
    } catch (error) {
      console.error('Error loading kindergartens:', error);
    } finally {
      setKgLoading(false);
    }
  }, []);

  // Обработчики для выбора садиков
  const handleKindergartenSearch = useCallback((searchValue) => {
    setKgSearchTerm(searchValue);
    loadKindergartens(searchValue, 0, false);
  }, [loadKindergartens]);

  const handleKindergartenLoadMore = useCallback(() => {
    if (!kgLoading && kgHasMore) {
      loadKindergartens(kgSearchTerm, kgPage + 1, true);
    }
  }, [kgLoading, kgHasMore, kgSearchTerm, kgPage, loadKindergartens]);

  const handleAddKindergarten = (kgId) => {
    const kgToAdd = kgById[kgId] || kgAllOptions.find(kg => kg.value === kgId);
    if (kgToAdd && !selectedKindergartensList.some(kg => kg.id === kgId)) {
        const name = kgToAdd.name || kgToAdd.label;
        setSelectedKindergartensList(prev => [...prev, { id: kgId, name }]);
        handleFilterChange('selectedKindergartens', [...(advancedFilters.selectedKindergartens || []), kgId]);
    }
  };

  const handleRemoveKindergarten = (kgId) => {
    setSelectedKindergartensList(prev => prev.filter(kg => kg.id !== kgId));
    handleFilterChange('selectedKindergartens', (advancedFilters.selectedKindergartens || []).filter(id => id !== kgId));
  };

  // Преобразование справочников в формат для CheckboxDropdown
  const benefitsOptions = benefitsList.map(b => ({
    value: b._id,
    label: b.name
  }));

  const disabilitiesOptions = disabilitiesList.map(d => ({
    value: d._id,
    label: d.label
  }));

  // Районы (статические)
  const districtOptionsStatic = [
    { value: 'Голосіївський', label: 'Голосіївський' },
    { value: 'Дарницький', label: 'Дарницький' },
    { value: 'Деснянський', label: 'Деснянський' },
    { value: 'Дніпровський', label: 'Дніпровський' },
    { value: 'Оболонський', label: 'Оболонський' },
    { value: 'Печерський', label: 'Печерський' },
    { value: 'Подільський', label: 'Подільський' },
    { value: 'Святошинський', label: 'Святошинський' },
    { value: 'Солом\'янський', label: 'Солом\'янський' },
    { value: 'Шевченківський', label: 'Шевченківський' }
  ];

  // Функция для обновления данных
  const updData = async () => {
    // Помечаем этот вызов как самый свежий. Старый isLoad-гвард убран:
    // раньше, если предыдущий запрос ещё выполнялся, новый вызов просто
    // игнорировался — и когда старый ответ приходил позже нового ввода,
    // на экране оставались результаты по предыдущему (уже неактуальному) поиску.
    const requestId = ++requestIdRef.current;
    setLoad(true);

    try {
      // Подготовка параметров для API
      const params = {
        search: debouncedGlobalSearch,
        page,
        pageSize,
        sortBy: sortConfig.key,
        sortDirection: sortConfig.direction,
      };
      
      
      
      if (advancedFilters.genders?.length) {
        params.genders = advancedFilters.genders;
      }

      if (advancedFilters.inviteStatuses?.length) {
        params.inviteStatuses = advancedFilters.inviteStatuses;
      }

      if (advancedFilters.inviteKindergartens?.length) {
        params.inviteKindergartens = advancedFilters.inviteKindergartens;
      }

      if (advancedFilters.benefitTypes?.length) {
        params.benefitTypes = advancedFilters.benefitTypes;
      }
      
      if (advancedFilters.onlyApprovedBenefits) {
        params.onlyApprovedBenefits = true;
      }
      
      if (advancedFilters.dissabilityTypes?.length) {
        params.dissabilityTypes = advancedFilters.dissabilityTypes;
      }
      
      if (advancedFilters.placeTypes?.length) {
        params.placeTypes = advancedFilters.placeTypes;
      }
      
      if (advancedFilters.createdFrom || advancedFilters.createdTo) {
        params.createdFrom = advancedFilters.createdFrom;
        params.createdTo = advancedFilters.createdTo;
      }
      
      if (advancedFilters.preferDateFrom || advancedFilters.preferDateTo) {
        params.preferDateFrom = advancedFilters.preferDateFrom;
        params.preferDateTo = advancedFilters.preferDateTo;
      }
      
      if (advancedFilters.inviteDateFrom || advancedFilters.inviteDateTo) {
        params.inviteDateFrom = advancedFilters.inviteDateFrom;
        params.inviteDateTo = advancedFilters.inviteDateTo;
      }
      
      if (advancedFilters.statuses?.length) {
        params.statuses = advancedFilters.statuses;
      }

      if (advancedFilters.ageRanges?.length) {
        params.ageRanges = advancedFilters.ageRanges;
      }
        
      if (advancedFilters.hasInvite) {
        params.hasInvite = true;
      }
      
      if (advancedFilters.hasDeclinedInvite) {
        params.hasDeclinedInvite = true;
      }
      
      if (advancedFilters.hasSelectedKindergartens) {
        params.hasSelectedKindergartens = true;
      }
      
      if (advancedFilters.districts?.length) {
        params.districts = advancedFilters.districts;
      }
      
      if (advancedFilters.selectedKindergartens?.length) {
        params.selectedKindergartens = advancedFilters.selectedKindergartens;
      }

      const { all_count, data } = await getRequestsAdvanced(params, isMy);

      // Если за время ожидания ответа успел уйти более новый запрос —
      // этот ответ устарел, применять его нельзя (иначе новые данные
      // перезатрутся старыми, которые просто пришли позже).
      if (requestId !== requestIdRef.current) {
        return;
      }

      // Преобразуем данные из API в нужный формат
      const formattedData = data.map(app => {
        const { criterias } = app;
        const status_ = app.statuses && app.statuses[app.statuses?.length - 1] && app.statuses[app.statuses?.length - 1]["name"] || "banned";
        const status = status_ === "need-check" && criterias?.length > 0 && criterias[0].values && criterias[0].values?.length > 0 ? "approved" : status_;
        
        // Вычисляем возраст
        let age = '';
        if (app.child?.birthDate) {
          const birthDate = new Date(app.child.birthDate);
          const today = new Date();
          let ageNum = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();
          if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
            ageNum--;
          }
          age = `${ageNum} р.`;
        }

        const getBenefitText = (benefits)=>{

            if(!benefits || !benefits[0]) return '-';
            
            const { benefitId, isApproved} = benefits[0];
            
            if(!benefitId) return '-';


            const benInfo = benefitsList.find(b=>b._id == benefitId);


            if(!benInfo) return '-';

            const { name } =benInfo;

            if(!name) return '-';

            return `${name.slice(0,25)}..., ${isApproved ? 'підтверджена' : 'непідтверджена'}`;
            
        }
        
        return {
          id: app._id,
          name: `${app.child?.firstName || ''} ${app.child?.lastName || ''}`.trim(),
          benefit: app.child?.benefits && app.child.benefits[0]?.benefitId || '',
          benefitText: getBenefitText(app.child?.benefits),
          certificate: app.child?.birthCertificate?.number || '',
          submitted: app.createdAt ? new Date(app.createdAt).toLocaleDateString('uk-UA') : '',
          sendFrom: app.preferDateFrom ? new Date(app.preferDateFrom).toLocaleDateString('uk-UA') : '',
          preferDate: app.preferDateFrom ? new Date(app.preferDateFrom).toLocaleDateString('uk-UA') : '',
          inviteDate: app.invite?.inviteDate ? new Date(app.invite.inviteDate).toLocaleDateString('uk-UA') : '',
          status,
          statusClass: getStatusClass(status),
          statusText: getStatusText(status),
          hasSelectedZDO: app.hasSelectedZDO || false,
          hasBenefits: app.hasBenefits || false,
          note: app.my_request || '',
          dissabilities: app.dissabilities?.specializationId ? 'ІРЦ' : '-',
          selectedKindergartens: app.selectedKindergartens?.map(kg => kg.name).join(', ') || '-',
          declineCount: app.invite?.inviteDecliningAmount || 0,
          age,
        };
      });

      setApplicationsData(formattedData);
      setAllCount(all_count);
    } catch (error) {
      if (requestId !== requestIdRef.current) {
        return; // тоже устаревший запрос — не затираем актуальное состояние ошибкой
      }
      console.error('Помилка при завантаженні заявок:', error);
      setApplicationsData([]);
      setAllCount(0);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoad(false);
      }
    }
  };


  // Функции для преобразования статусов
  const getStatusClass = (status) => {
    const statusMap = {
      'new': 'status-new',
      'approved': 'status-approved',
      'rejected': 'status-rejected',
      'pending': 'status-pending',
      'processing': 'status-processing',
      'need-check': 'status-pending',
      'invite-sent': 'status-processing',
      'invite-accepted': 'status-approved',
      'invite-declined': 'status-rejected',
      'removed': 'status-rejected',
      'banned': 'status-rejected',
      'child-in-kindergarten': 'status-approved',
    };
    return statusMap[status] || 'status-pending';
  };

  const getStatusText = (status) => {
    const statusMap = {
      'need-check': 'Садочки не обрані',
      'approved': 'Садочки обрано',
      'invite-sent': 'Надіслано запрошення',
      'invite-accepted': 'Прийнято запрошення',
      'invite-declined': 'Відхилено запрошення',
      'removed': 'Видалена',
      'banned': 'Заявка заблокована',
      'child-in-kindergarten': 'Дитину оформили',
    };
    return statusMap[status] || status;
  };

  // Функция сортировки
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
    setPage(0);
  };

  // Обработчики фильтров
  const handleFilterChange = (key, value) => {
    if (key === 'globalSearch') {
      setGlobalSearch(value);
      setPage(0);
    } else if (key === 'reset') {
      setAdvancedFilters({});
      setActiveFilterCount(0);
    } else {
      setAdvancedFilters(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleApplyFilters = () => {
    // Подсчёт количества активных фильтров
    let count = 0;
    Object.entries(advancedFilters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value) && value.length === 0) return;
        if (value === true || value === false) {
          if (value) count++;
          return;
        }
        count++;
      }
    });
    setActiveFilterCount(count);
    setPage(0);
    updData();
  };

  const handleResetAllFilters = () => {
    setAdvancedFilters({});
    setActiveFilterCount(0);
    setPage(0);
  };

  // Обработчики для колонок
  const handleColumnToggle = (columnKey) => {
    setVisibleColumns(prev => {
      const newCols = prev.includes(columnKey)
        ? prev.filter(k => k !== columnKey)
        : [...prev, columnKey];
      localStorage.setItem('applications_visible_columns', JSON.stringify(newCols));
      return newCols;
    });
  };

  const handleSelectAllColumns = () => {
    const allKeys = availableColumns.map(c => c.key);
    setVisibleColumns(allKeys);
    localStorage.setItem('applications_visible_columns', JSON.stringify(allKeys));
  };

  const handleClearAllColumns = () => {
    setVisibleColumns([]);
    localStorage.setItem('applications_visible_columns', JSON.stringify([]));
  };

  // Эффекты для загрузки данных
  useEffect(() => {
    updData();
  }, [page, pageSize, sortConfig, debouncedGlobalSearch]);

  useEffect(() => {
    updData();
  }, [advancedFilters]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      // cleanup
    };
  }, []);

  const getSortIcon = (columnKey) => {
    if (columnKey !== sortConfig.key) return faSort;
    return sortConfig.direction === 'asc' ? faSortUp : faSortDown;
  };

  const handleMenuToggle = (index) => {
    setActiveMenuIndex(activeMenuIndex === index ? null : index);
  };

  const handleMenuClose = () => {
    setActiveMenuIndex(null);
  };

  const handleViewApplication = (application) => {
    setSelectedApplication(application);
    setIsModalOpen(true);
  };

  const handleEditApplication = async (application) => {
    const data = await applicationApi.getApplicationById(application.id);
    return onEdit(convertReqToForm(data));
  };

  const handleActionSelect = (actionId, application) => {
    if (actionId === 'view') {
      handleViewApplication(application);
    } else if (actionId === 'edit') {
      handleEditApplication(application);
    }
    setActiveMenuIndex(null);
  };

  const handleNewApplication = () => newApplication();

  // Пагинация
  const totalPages = Math.ceil(allcount / pageSize);
  const getVisiblePages = () => {
    const maxVisible = 5;
    const half = Math.floor(maxVisible / 2);
    let start = Math.max(0, page - half);
    let end = Math.min(totalPages, start + maxVisible);
    
    if (end - start < maxVisible && start > 0) {
      start = Math.max(0, end - maxVisible);
    }
    
    return Array.from({ length: end - start }, (_, i) => start + i);
  };

  const visiblePages = getVisiblePages();

  // Меню действий
  const ActionsMenu = ({ onActionSelect, onClose, application }) => {
    const actions = [
      { id: 'view', label: 'Перегляд', icon: faEye }
    ];

    if (isMy) actions.push({ id: 'edit', label: 'Редагувати', icon: faEdit });

    return (
      <div className="actions-dropdown">
        {actions.map((action) => (
          <button
            key={action.id}
            className="dropdown-item d-flex align-items-center gap-2 p-2"
            onClick={() => onActionSelect(action.id, application)}
            style={{
              border: 'none',
              backgroundColor: 'transparent',
              width: '100%',
              textAlign: 'left',
              fontSize: '14px',
              cursor: 'pointer'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <FontAwesomeIcon icon={action.icon} style={{ width: '16px', color: '#6c757d' }} />
            <span style={{ color: '#212529' }}>{action.label}</span>
          </button>
        ))}
        <div style={{ borderTop: '1px solid #dee2e6', padding: '8px' }}>
          <button
            className="btn btn-sm btn-outline-secondary w-100"
            onClick={onClose}
            style={{ fontSize: '12px' }}
          >
            Скасувати
          </button>
        </div>
      </div>
    );
  };

  // Фильтруем колонки для отображения
  const displayColumns = availableColumns.filter(col => visibleColumns.includes(col.key));

  // Функция для рендера значения ячейки
  const renderCellValue = (application, columnKey) => {
    const value = application[columnKey];
    
    if (columnKey === 'name') {
      return (
        <a 
          href="#" 
          className="child-name-link"
          onClick={(e) => {
            e.preventDefault();
            handleActionSelect('view', application);
          }}
        >
          {value}
        </a>
      );
    }
    
    if (columnKey === 'statusText') {
      return (
        <span className={`status ${application.statusClass}`}>
          {value}
        </span>
      );
    }
    
    if (columnKey === 'declineCount' && value > 0) {
      return <span className="text-danger fw-bold">{value}</span>;
    }
    
    if (value === '-' || !value) {
      return <span className="text-muted">—</span>;
    }
    
    return value;
  };

  return (
    <>
      <main className="queuetable-main-content queuetable-page">
        <div className="container-fluid">
          <div className="queuetable-content-wrapper">
            {/* Заголовок страницы */}
            <div className="queuetable-year-bar d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-3 flex-wrap">
                <div>
                  <span className="queuetable-label text-white-50">
                    {isMy ? 'Заявки, що подані мною' : 'Заявки на зарахування'}
                  </span>
                </div>
              </div>
              {isMy && (
                <div className="button-wrapper">
                  <Button className="new-app-btn" onClick={handleNewApplication}>
                    <i className="fa-solid fa-plus me-2"></i>Нова заявка
                  </Button>
                </div>
              )}
            </div>

            {/* Расширенные фильтры и выбор колонок */}
            <AdvancedFiltersAndColumns
              filters={advancedFilters}
              onFilterChange={handleFilterChange}
              onApplyFilters={handleApplyFilters}
              activeFilterCount={activeFilterCount}
              availableColumns={availableColumns}
              visibleColumns={visibleColumns}
              onColumnToggle={handleColumnToggle}
              onSelectAllColumns={handleSelectAllColumns}
              onClearAllColumns={handleClearAllColumns}
              onResetAll={handleResetAllFilters}
              districtOptions={districtOptionsStatic}
              benefitsOptions={benefitsOptions}
              disabilitiesOptions={disabilitiesOptions}
              kindergartenOptions={kgAllOptions}
              onKindergartenSearch={handleKindergartenSearch}
              onKindergartenLoadMore={handleKindergartenLoadMore}
              kindergartenLoading={kgLoading}
              kindergartenHasMore={kgHasMore}
              kgById={kgById}
              inviteKindergartenOptions={kgAllOptions}
              inviteKindergartenLoading={kgLoading}
              inviteKindergartenHasMore={kgHasMore}
              onInviteKindergartenSearch={handleKindergartenSearch}
              onInviteKindergartenLoadMore={handleKindergartenLoadMore}
              inviteKgById={kgById}
            />

            {/* Таблица */}
            <div className="card applications-table-card">
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table applications-table align-middle mb-0">
                    <thead>
                      <tr>
                        {displayColumns.map((column) => (
                          <th 
                            key={column.key}
                            style={{ width: column.width }}
                            onClick={() => column.sortable && handleSort(column.key)}
                          >
                            <div className="d-flex align-items-center">
                              {column.label}
                              {column.sortable && (
                                <FontAwesomeIcon 
                                  icon={getSortIcon(column.key)} 
                                  className="ms-1" 
                                  style={{ fontSize: '12px', cursor: 'pointer' }}
                                />
                              )}
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {isLoad ? (
                        <tr>
                          <td colSpan={displayColumns.length} className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                              <span className="visually-hidden">Завантаження...</span>
                            </div>
                            <p className="mt-3 mb-0 text-muted">Завантаження заявок...</p>
                          </td>
                        </tr>
                      ) : applicationsData.length > 0 ? (
                        applicationsData.map((application, index) => (
                          <tr key={application.id}>
                            {displayColumns.map((column) => {
                              if (column.key === 'actions') {
                                return (
                                  <td key={column.key} className="text-end">
                                    <div className="actions-menu-container" style={{ position: 'relative', display: 'inline-block' }}>
                                      <button
                                        className="btn btn-sm btn-link text-decoration-none p-0"
                                        onClick={() => handleMenuToggle(index)}
                                        style={{ color: '#0069ff' }}
                                        disabled={isLoad}
                                      >
                                        <FontAwesomeIcon icon={faEllipsisVertical} />
                                      </button>
                                      {activeMenuIndex === index && (
                                        <ActionsMenu 
                                          onActionSelect={handleActionSelect}
                                          onClose={handleMenuClose}
                                          application={application}
                                        />
                                      )}
                                    </div>
                                  </td>
                                );
                              }
                              
                              return (
                                <td key={column.key}>
                                  {renderCellValue(application, column.key)}
                                </td>
                              );
                            })}
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={displayColumns.length} className="text-center py-5">
                            <div className="text-muted">
                              <p className="mb-2">Заявки не знайдено</p>
                              <small>Спробуйте змінити параметри пошуку або фільтри</small>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Пагинация */}
                {totalPages > 1 && (
                  <div className="d-flex align-items-center justify-content-between mt-4 applications-pagination-wrapper">
                    <div className="applications-pagination-info">
                      Всього {allcount} {requestword(allcount)}
                    </div>
                    <nav aria-label="Page navigation">
                      <ul className="pagination applications-pagination mb-0">
                        <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setPage(0)}
                            disabled={page === 0 || isLoad}
                          >
                            «
                          </button>
                        </li>
                        <li className={`page-item ${page === 0 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setPage(Math.max(0, page - 1))}
                            disabled={page === 0 || isLoad}
                          >
                            &lt;
                          </button>
                        </li>
                        
                        {visiblePages.map((pageNum) => (
                          <li 
                            key={pageNum} 
                            className={`page-item ${pageNum === page ? 'active' : ''}`}
                          >
                            <button 
                              className="page-link"
                              onClick={() => setPage(pageNum)}
                              disabled={isLoad}
                            >
                              {pageNum + 1}
                            </button>
                          </li>
                        ))}
                        
                        <li className={`page-item ${page === totalPages - 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                            disabled={page === totalPages - 1 || isLoad}
                          >
                            &gt;
                          </button>
                        </li>
                        <li className={`page-item ${page === totalPages - 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setPage(totalPages - 1)}
                            disabled={page === totalPages - 1 || isLoad}
                          >
                            »
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                )}
                
                {totalPages <= 1 && allcount > 0 && (
                  <div className="d-flex align-items-center justify-content-between mt-4 applications-pagination-wrapper">
                    <div className="applications-pagination-info">
                      Всього {allcount} {requestword(allcount)}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>

      <ApplicationModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        applicationId={selectedApplication?.id}
        mode={mode}
        userRole={userRole}
      />
    </>
  );
};

export default ApplicationsList;